import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  OnboardingMessages,
  OnboardingMessageKey,
  interpolate,
} from './messages';
import { en } from './en';
import { es } from './es';
import { ptBR } from './pt-BR';

/* ────────────────────────────────────────────────────────────────
 *  Supported languages
 *
 *  When Backstage adopts its own i18n layer, replace this context
 *  with `createTranslationRef` + `useTranslationRef`.  The `t()`
 *  call-sites remain unchanged.
 * ──────────────────────────────────────────────────────────────── */

export type OnboardingLanguage = 'en' | 'es' | 'pt-BR';

const LANGUAGE_STORAGE_KEY = 'onboarding_language';

const catalogues: Record<OnboardingLanguage, OnboardingMessages> = {
  en,
  es,
  'pt-BR': ptBR,
};

export const languageLabels: Record<OnboardingLanguage, string> = {
  en: 'English',
  es: 'Español',
  'pt-BR': 'Português',
};

/* ── helpers ──────────────────────────────────────────────────── */

function getStoredLanguage(): OnboardingLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && stored in catalogues) return stored as OnboardingLanguage;
  } catch {
    /* ignore */
  }
  return 'en';
}

/* ── context ─────────────────────────────────────────────────── */

interface TranslationContextValue {
  language: OnboardingLanguage;
  setLanguage: (lang: OnboardingLanguage) => void;
  /**
   * Translate a message key, optionally interpolating `{{placeholder}}`
   * tokens.  The API mirrors Backstage's `useTranslationRef` so the
   * call-sites won't need to change when the lib i18n ships.
   */
  t: (
    key: OnboardingMessageKey,
    values?: Record<string, string | number>,
  ) => string;
}

const TranslationContext = createContext<TranslationContextValue>({
  language: 'en',
  setLanguage: () => {},
  t: (key: OnboardingMessageKey) => en[key],
});

export const useOnboardingTranslation = () => useContext(TranslationContext);

export const OnboardingTranslationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [language, setLanguageState] = useState<OnboardingLanguage>(
    getStoredLanguage,
  );

  const setLanguage = useCallback((lang: OnboardingLanguage) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key: OnboardingMessageKey, values?: Record<string, string | number>) => {
      const msg = catalogues[language]?.[key] ?? en[key] ?? key;
      return values ? interpolate(msg, values) : msg;
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};
